/**
 * @author        david.loehr@zumper.com (David Loehr)
 * @copyright     Copyright (c) 2023, Zumper
 * @description   AddressPoliciesAndFees.tsx
 */

import {
  selectHasShow0ForFees,
  selectMajorityPets,
  selectParkingData,
  selectPetPolicy,
  selectShortAddress,
} from '@zcode-ui/perm-address'
import { PetPolicy } from '@zcode/bizos'
import { isTruthy } from '@zcode/utils'
import React, { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { PoliciesAndFees } from '../../../components/detail/Content/PoliciesAndFees/PoliciesAndFees'

export const AddressPoliciesAndFees = () => {
  const parkingData = useSelector(selectParkingData)
  const hasShow0ForFees = useSelector(selectHasShow0ForFees)
  const { catsOk, dogsOk } = useSelector(selectMajorityPets)
  const petPolicy = useSelector(selectPetPolicy)
  // - petPolicy contains the petPolicy of **one** listing
  // - catsOk / dogsOk indicate whether a majority of the address's listings:
  //   - allow cats / dogs (if the value is true)
  //   - explicitly disallow cats / dogs (if the value is false)
  //   - null means that there's no majority
  // - we mostly want to display that single-listing petPolicy, but "Allowed" should be
  //   based on that "majority"
  const combinedPetPolicy = useMemo(() => {
    let petPolicies = petPolicy && !Array.isArray(petPolicy) ? [petPolicy] : petPolicy
    if (!petPolicies || (Array.isArray(petPolicies) && !petPolicies.length)) {
      return [
        typeof catsOk === 'boolean' && new PetPolicy({ ok: catsOk, type: 'cat' }),
        typeof dogsOk === 'boolean' && new PetPolicy({ ok: dogsOk, type: 'dog' }),
      ].filter(isTruthy)
    }
    for (let i = 0; i < petPolicies.length; i++) {
      const policy: PetPolicy = petPolicies[i]
      if (policy.type === 'cat' && typeof catsOk === 'boolean' && policy.ok !== catsOk) {
        petPolicies = [
          ...petPolicies.slice(0, i),
          new PetPolicy({ ...policy._data, ok: catsOk }),
          ...petPolicies.slice(i + 1, petPolicies.length),
        ]
      } else if (policy.type === 'dog' && typeof dogsOk === 'boolean' && policy.ok !== dogsOk) {
        petPolicies = [
          ...petPolicies.slice(0, i),
          new PetPolicy({ ...policy._data, ok: dogsOk }),
          ...petPolicies.slice(i + 1, petPolicies.length),
        ]
      }
    }
    return petPolicy
  }, [catsOk, dogsOk, petPolicy])

  const shortAddress = useSelector(selectShortAddress)
  const trackViewPoliciesAndFees = useCallback(() => {
    // TODO: tracking
    // dispatch(mixpanelViewSection(DETAIL.POLICIES_AND_FEES))
    // dispatch(
    //   trackMightBeDetailEvent({ name: toTraktorEventName('DETAIL_POLICIES_AND_FEES_IMPRESSION') })
    // )
  }, [])
  return (
    <PoliciesAndFees
      fees={null}
      hasShow0ForFees={hasShow0ForFees}
      headingText={shortAddress ? `Policies at ${shortAddress}` : 'Policies'}
      parkingData={parkingData}
      petPolicy={combinedPetPolicy}
      pets={null}
      trackViewPoliciesAndFees={trackViewPoliciesAndFees}
    />
  )
}

// eslint-disable-next-line import/no-default-export
export default AddressPoliciesAndFees
